import ZTable, { IColumnType, IFilterItem, ITableRef } from '@/components/ZTable';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { Button, Col, Form, Input, message, Modal, Row, Select } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { PrinterCompanyType, PrintType } from '@/constants';
import { PartnerAccountDto, StationPrinterDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: () => void;
  stationId: string;
};

const columns: IColumnType<StationPrinterDto>[] = [
  {
    title: 'stationsPage_PrinterCompany',
    dataIndex: 'printerCompany',
    ellipsis: true,
    render: v => PrinterCompanyType.__getValue(v),
  },
  {
    title: 'base.IPAddress',
    dataIndex: 'ipAddress',
    ellipsis: true,
  },
  {
    title: 'stationsPage_DeviceName',
    dataIndex: 'deviceName',
    ellipsis: true,
  },
  {
    title: 'stationsPage_PrintType',
    dataIndex: 'printType',
    ellipsis: true,
    render: v => PrintType.__getValue(v),
  },
];
const filters: Array<IFilterItem> = [
  {
    type: 'TEXT',
    label: 'stationsPage_DeviceName',
    name: 'deviceName',
  },
  {
    type: 'LIST',
    label: 'stationsPage_PrinterCompany',
    name: 'printerCompany',
    listData: PrinterCompanyType.__getSelectOptions(),
  },
  {
    type: 'LIST',
    label: 'stationsPage_PrintType',
    name: 'printType',
    listData: PrintType.__getSelectOptions(),
  },
];

export default function PrinterConfigurationsModal({ onClose, stationId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [isNewForm, setIsNewForm] = React.useState(false);

  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = React.useState(false);
  const createEntity = values => {
    setLoading(true);
    apiSdk.StationPrinterService.create({ body: { ...values, stationId: stationId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.StationPrinterService.update({ body: { ...values, stationId: stationId } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      style={{ top: 10, minHeight: 500 }}
      visible={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={t('stationsPage_PrinterConfigurations')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <ZTable<PartnerAccountDto>
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/fleets/station-printers/get-station-printers"
        bordered
        defaultFilter={{
          stationId: stationId,
        }}
        filters={filters}
        order_by="deviceName"
        order_by_asc={false}
        allowEdit={true}
        allowDelete={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue(record);
        }}
        delete_url="/fleets/station-printers/delete"
        searchBtnAlignLeft
        extraBtns={
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => {
              form.resetFields();
              setFormVisible(true);
            }}
          >
            {t('createNew')}
          </Button>
        }
      />
      {formVisible ? (
        <Modal
          style={{ top: 10 }}
          visible={true}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('stationsPage_AddPrinter') : t('stationsPage_UpdatePrinter')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateEntity(values);
                } else {
                  createEntity(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10, xs: 24 }}>
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('stationsPage_PrinterCompany')} name="printerCompany" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={PrinterCompanyType.__getSelectOptions()} />
                </Form.Item>
                <Form.Item label={t('stationsPage_DeviceName')} name="deviceName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('base.IPAddress')} name="ipAddress" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t('stationsPage_PrintType')} name="printType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select options={PrintType.__getSelectOptions()} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      ) : null}
    </Modal>
  );
}
// "printerCompany": 1,
// "printType": 1,
// "ipAddress": "string",
// "deviceName": "string"
